import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchWpsSubmission, generateSifApi, acknowledgeWpsApi, rejectWpsApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import Chip, { type ChipVariant } from './Chip';
import Button from './Button';
import Modal from './Modal';
import Alert from './Alert';
import type { RegulatoryStatus } from '../types';

/**
 * Per-run WPS operations view (SKPEOPLE-UICC-001 CH-U4).
 *
 * For a WPS-mandated org this REPLACES the mark-paid button rather than
 * disabling it: "never a disabled button without explanation — the status IS the
 * control." Payment is a consequence of MOHRE accepting the SIF, so the thing on
 * screen is the filing, not a payment toggle.
 */

const STATUS_VARIANT: Record<RegulatoryStatus, ChipVariant> = {
  PENDING: 'warn',
  RETRYING: 'warn',
  SENT: 'info',
  DEAD_LETTER: 'danger',
};

/** Days to the MOHRE deadline; negative means it has passed. */
function daysToDeadline(deadlineAt: string | null): number | null {
  if (!deadlineAt) return null;
  return Math.floor((new Date(deadlineAt).getTime() - Date.now()) / 86_400_000);
}

export default function WpsPanel({ runId }: { runId: string }) {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');

  const { data: submission, isLoading, isError } = useQuery({
    queryKey: qk.wpsSubmission(runId),
    queryFn: () => fetchWpsSubmission(runId),
    enabled: !!runId,
    // A 404 simply means this org is not WPS-mandated — not worth retrying.
    retry: false,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.wpsSubmission(runId) });
    queryClient.invalidateQueries({ queryKey: qk.payrollRuns() });
  };

  const sifMutation = useMutation({
    mutationFn: () => generateSifApi(runId),
    onSuccess: () => { invalidate(); toast('SIF generated and submitted'); },
    onError: (e: Error) => toast(e.message || 'Could not generate the SIF', 'danger'),
  });

  const ackMutation = useMutation({
    mutationFn: () => acknowledgeWpsApi(runId),
    onSuccess: () => { invalidate(); toast('Acceptance recorded — run marked paid'); },
    onError: (e: Error) => toast(e.message || 'Could not record acceptance', 'danger'),
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectWpsApi(runId, reason.trim()),
    onSuccess: () => {
      invalidate();
      toast('Rejection recorded — the run is re-opened for amendment', 'warn');
      setReason('');
      setRejectOpen(false);
    },
    onError: (e: Error) => toast(e.message || 'Could not record the rejection', 'danger'),
  });

  // No submission = not a WPS org. Render nothing rather than an empty panel.
  if (isLoading || isError || !submission) return null;

  const days = daysToDeadline(submission.deadlineAt);

  return (
    <div className="t-card" style={{ marginBottom: 16 }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>Wage Protection System</h3>
          <Chip variant={STATUS_VARIANT[submission.status]} dot>{submission.status.toLowerCase()}</Chip>
          {submission.acknowledgedAt && <Chip variant="success">acknowledged</Chip>}
          {submission.rejectionReason && !submission.acknowledgedAt && <Chip variant="danger">rejected</Chip>}
        </div>

        {days !== null && !submission.acknowledgedAt && (
          <p style={{
            fontSize: 'var(--fs-sm)',
            color: days < 0 ? 'var(--danger, #dc2626)' : days <= 3 ? 'var(--warn, #b45309)' : 'var(--ink-3)',
            marginBottom: 8,
          }}>
            {days < 0
              ? `MOHRE deadline passed ${Math.abs(days)} day(s) ago`
              : `MOHRE deadline in ${days} day(s) · ${submission.deadlineAt?.slice(0, 10)}`}
          </p>
        )}

        {submission.rejectionReason && (
          // Verbatim: a paraphrased bank rejection is useless to whoever has to
          // correct the file.
          <Alert variant="danger" style={{ marginBottom: 10 }}>
            {submission.rejectionReason}
          </Alert>
        )}

        {submission.sifDriveRef && (
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginBottom: 10 }}>
            SIF file: <span className="mono">{submission.sifDriveRef}</span>
            {submission.attempts > 0 && ` · attempt ${submission.attempts + 1}`}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!submission.acknowledgedAt && (
            <Button variant="accent" size="sm" disabled={sifMutation.isPending}
              onClick={() => sifMutation.mutate()}>
              {submission.sifDriveRef ? 'Regenerate SIF' : 'Generate SIF'}
            </Button>
          )}
          {!submission.acknowledgedAt && (
            <>
              <Button variant="success" size="sm" disabled={ackMutation.isPending}
                onClick={() => ackMutation.mutate()}>
                Record acceptance
              </Button>
              <Button variant="danger" size="sm" onClick={() => setRejectOpen(true)}>
                Record rejection
              </Button>
            </>
          )}
        </div>

        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 10 }}>
          This organisation is WPS-mandated, so the run is marked paid when MOHRE accepts the SIF —
          there is no manual mark-paid step.
        </p>
      </div>

      {rejectOpen && (
        <Modal
          title="Record a WPS rejection"
          onClose={() => setRejectOpen(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button variant="danger" disabled={rejectMutation.isPending}
                onClick={() => {
                  if (!reason.trim()) { toast('Paste the rejection reason', 'warn'); return; }
                  rejectMutation.mutate();
                }}>
                Record rejection
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
              Paste the reason exactly as the agent bank gave it. This re-opens the run so the
              underlying data can be corrected and re-filed.
            </p>
            <textarea className="t-input" rows={4} value={reason}
              onChange={(e) => setReason(e.target.value)} />
          </div>
        </Modal>
      )}
    </div>
  );
}
